import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Grid,
  Card,
  CardContent,
  Typography,
  Avatar,
  Button,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemButton,
  Chip,
  Paper,
  useTheme,
  useMediaQuery,
  Drawer,
  IconButton,
  AppBar,
  Toolbar,
  Menu,
  MenuItem,
  BottomNavigation,
  BottomNavigationAction,
  Modal,
  TextField
} from '@mui/material';
import {
  Person,
  ShoppingBag,
  Settings,
  Favorite,
  LocationOn,
  Payment,
  Security,
  Notifications,
  Edit,
  History,
  Menu as MenuIcon,
  ArrowBack,
  Key,
  Email, Numbers, Add,
  Business, AccountTree, AddLocationAlt, LocationPin,
  PinDrop
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import UserNavbar from './UserNavbar';
import { get_user_data, update_user, create_address } from '../../services/profileService';
import { useNotification } from '../../contexts/NotificationContext'
import ProfileOrderTable from '../../components/user/ProfileOrderTable'
import ProfileAddressTable from '../../components/user/ProfileAddressTable'



const editModalStyle = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 400,
  bgcolor: 'background.paper',
  // border: '2px solid #000',
  boxShadow: 24,
  p: 4,
  borderRadius: '25px'
};

const ProfilePage = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isSmallMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();
  const location = useLocation();
  const { showError, showSuccess } = useNotification();

  const [activeSection, setActiveSection] = useState('profile');
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);
  const [mobileMenuAnchor, setMobileMenuAnchor] = useState(null);
  const [userData, setUserData] = useState({})
  const [addressData, setAddressData] = useState([])
  const [editModalOpen, setEditModalOpen] = useState(false)
  const [addressModalOpen, setAddressModalOpen] = useState(false)


  const navigationItems = [
    { id: 'profile', label: 'Profile', icon: <Person /> },
    { id: 'orders', label: 'My Orders', icon: <ShoppingBag /> },
    { id: 'addresses', label: 'Addresses', icon: <LocationOn /> },
    { id: 'payments', label: 'Payment Methods', icon: <Payment /> },
    { id: 'favorites', label: 'Favorites', icon: <Favorite /> },
    { id: 'notifications', label: 'Notifications', icon: <Notifications /> },
    { id: 'security', label: 'Security', icon: <Security /> },
    { id: 'settings', label: 'Settings', icon: <Settings /> },
  ];

  // Mobile bottom navigation items (limited to 4 main items)
  const mobileNavItems = [
    { id: 'profile', label: 'Profile', icon: <Person /> },
    { id: 'orders', label: 'Orders', icon: <ShoppingBag /> },
    { id: 'favorites', label: 'Favorites', icon: <Favorite /> },
    { id: 'menu', label: 'More', icon: <MenuIcon /> },
  ];

  useEffect(() => {
    // Set active section based on current path
    const currentItem = navigationItems.find(item =>
      location.pathname === item.path || location.pathname.startsWith(item.path)
    );
    if (currentItem) {
      setActiveSection(currentItem.id);
    }
  }, [location.pathname]);

  useEffect(() => {
    async function fetchUserData() {
      try {
        let user = JSON.parse(sessionStorage.getItem('user'))
        let data = {
          "id": parseInt(user['id'])
        }
        const response = await get_user_data(data);
        setUserData(response)
      }
      catch (error) {
        console.error("Error on Getting User => ", error);
      }
    }
    fetchUserData()
  }, [addressModalOpen])

  const handleNavigation = (item) => {
    setActiveSection(item.id);
    navigate(item.path);
    setMobileDrawerOpen(false);
    setMobileMenuAnchor(null);
  };

  const handleMobileMenuOpen = (event) => {
    setMobileMenuAnchor(event.currentTarget);
  };

  const handleMobileMenuClose = () => {
    setMobileMenuAnchor(null);
  };

  const isActive = (itemId) => {
    return activeSection === itemId;
  };

  const handleEditUserData = (e) => {
    let updateData = {
      ...userData,
      [e.target.name]: e.target.value
    }

    setUserData(updateData)

  }


  const updateUserData = async () => {
    try {
      const response = await update_user(userData);
      showSuccess(response.message)
      setEditModalOpen(false)
    }
    catch (error) {
      console.error("Error Happened => ", error);
      showError("Something Went Wrong, Please Try Again Later")
      setEditModalOpen(false)
    }
  }

  // Main content components for different sections
  const renderMainContent = () => {
    switch (activeSection) {
      case 'profile':
        return <ProfileSection userData={userData} isMobile={isMobile} editModalOpen={editModalOpen} setEditModalOpen={setEditModalOpen} />;
      case 'orders':
        return <OrdersSection isMobile={isMobile} />;
      case 'addresses':
        return <AddressesSection isMobile={isMobile} addressModalOpen={addressModalOpen} setAddressModalOpen={setAddressModalOpen} />;
      case 'payments':
        return <PaymentsSection isMobile={isMobile} />;
      case 'favorites':
        return <FavoritesSection isMobile={isMobile} />;
      case 'notifications':
        return <NotificationsSection isMobile={isMobile} />;
      case 'security':
        return <SecuritySection isMobile={isMobile} />;
      case 'settings':
        return <SettingsSection isMobile={isMobile} />;
      default:
        return <ProfileSection userData={userData} isMobile={isMobile} />;
    }
  };

  // Mobile Sidebar Drawer
  const renderMobileDrawer = () => (
    <Drawer
      anchor="left"
      open={mobileDrawerOpen}
      onClose={() => setMobileDrawerOpen(false)}
      sx={{
        '& .MuiDrawer-paper': {
          width: isSmallMobile ? '100%' : 280,
          boxSizing: 'border-box',
        },
      }}
    >
      <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Typography variant="h6">Menu</Typography>
          <IconButton onClick={() => setMobileDrawerOpen(false)}>
            <ArrowBack />
          </IconButton>
        </Box>

        {/* User Info in Drawer */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
          <Avatar
            sx={{
              width: 50,
              height: 50,
              bgcolor: 'primary.main',
            }}
          >
            {userData.name?.charAt(0) || 'U'}
          </Avatar>
          <Box>
            <Typography variant="subtitle1" fontWeight="600">
              {userData.name}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {userData.loyaltyPoints} Points
            </Typography>
          </Box>
        </Box>
      </Box>

      <List sx={{ p: 1 }}>
        {navigationItems.map((item) => (
          <ListItem key={item.id} disablePadding sx={{ mb: 0.5 }}>
            <ListItemButton
              onClick={() => handleNavigation(item)}
              selected={isActive(item.id)}
              sx={{
                borderRadius: 1,
                mx: 0.5,
                py: 1.5,
                '&.Mui-selected': {
                  bgcolor: 'primary.light',
                  color: 'primary.main',
                  '&:hover': {
                    bgcolor: 'primary.light',
                  },
                },
              }}
            >
              <ListItemIcon
                sx={{
                  minWidth: 40,
                  color: isActive(item.id) ? 'primary.main' : 'text.secondary',
                }}
              >
                {item.icon}
              </ListItemIcon>
              <ListItemText
                primary={item.label}
                primaryTypographyProps={{
                  fontSize: '0.95rem',
                  fontWeight: isActive(item.id) ? 600 : 400,
                }}
              />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
    </Drawer>
  );

  // Mobile App Bar
  const renderMobileAppBar = () => (
    <AppBar
      position="sticky"
      color="default"
      elevation={1}
      sx={{ bgcolor: 'background.paper' }}
    >
      <Toolbar>
        <IconButton
          edge="start"
          sx={{ mr: 2 }}
          onClick={() => setMobileDrawerOpen(true)}
        >
          <MenuIcon />
        </IconButton>
        <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
          {navigationItems.find(item => item.id === activeSection)?.label || 'Profile'}
        </Typography>
        <IconButton onClick={handleMobileMenuOpen}>
          <Person />
        </IconButton>
      </Toolbar>
    </AppBar>
  );

  return (
    <>
      <UserNavbar />

      {isMobile && renderMobileAppBar()}
      {isMobile && renderMobileDrawer()}

      <Box sx={{
        minHeight: '100vh',
        bgcolor: 'background.default',
        py: isMobile ? 1 : 3,
        pb: isMobile ? 8 : 3 // Add padding for bottom navigation
      }}>
        <Container maxWidth="xl" sx={{ px: isMobile ? 1 : 3 }}>
          <Grid container spacing={isMobile ? 1 : 3}>
            {/* Sidebar Navigation - Hidden on mobile */}
            {!isMobile && (
              <Grid item xs={12} md={3} lg={2.5}>
                <Paper
                  elevation={0}
                  sx={{
                    borderRadius: 2,
                    overflow: 'hidden',
                    border: '1px solid',
                    borderColor: 'divider',
                    bgcolor: 'background.paper',
                  }}
                >
                  {/* User Info Card */}
                  <Box sx={{ p: 3, textAlign: 'center', borderBottom: '1px solid', borderColor: 'divider' }}>
                    {/* <Avatar
                      sx={{
                        width: 80,
                        height: 80,
                        mx: 'auto',
                        mb: 2,
                        bgcolor: 'primary.main',
                        fontSize: '2rem',
                      }}
                    >
                      {userData.name?.charAt(0) || 'U'}
                    </Avatar> */}
                    <Typography variant="h6" fontWeight="600" gutterBottom>
                      {userData.username}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      {userData.email}
                    </Typography>
                    <Chip
                      label={`${userData.loyaltyPoints} Points`}
                      size="small"
                      color="primary"
                      variant="outlined"
                      sx={{ mt: 1 }}
                    />
                  </Box>

                  {/* Navigation List */}
                  <List sx={{ p: 1 }}>
                    {navigationItems.map((item) => (
                      <ListItem key={item.id} disablePadding sx={{ mb: 0.5 }}>
                        <ListItemButton
                          onClick={() => handleNavigation(item)}
                          selected={isActive(item.id)}
                          sx={{
                            borderRadius: 1,
                            mx: 1,
                            py: 1.5,
                            '&.Mui-selected': {
                              bgcolor: 'primary.light',
                              color: 'white',
                              '&:hover': {
                                bgcolor: 'primary.light',
                              },
                              '& .MuiListItemIcon-root': {
                                color: 'white',
                              },
                            },
                            '&:hover': {
                              bgcolor: 'action.hover',
                            },
                          }}
                        >
                          <ListItemIcon
                            sx={{
                              minWidth: 40,
                              color: isActive(item.id) ? 'white' : 'text.secondary',
                            }}
                          >
                            {item.icon}
                          </ListItemIcon>
                          <ListItemText
                            primary={item.label}
                            primaryTypographyProps={{
                              fontSize: '0.95rem',
                              fontWeight: isActive(item.id) ? 600 : 400,
                            }}
                          />
                        </ListItemButton>
                      </ListItem>
                    ))}
                  </List>
                </Paper>
              </Grid>
            )}

            {/* Main Content Area */}
            <Grid item xs={12} md={isMobile ? 12 : 9} lg={isMobile ? 12 : 9.5}>
              <Paper
                elevation={isMobile ? 0 : 0}
                sx={{
                  borderRadius: isMobile ? 0 : 2,
                  border: isMobile ? 'none' : '1px solid',
                  borderColor: 'divider',
                  bgcolor: 'background.paper',
                  minHeight: isMobile ? 'calc(100vh - 120px)' : '600px',
                }}
              >
                {renderMainContent()}

                {/* -----------  EDIT MODAL --------------- */}
                <Modal
                  open={editModalOpen}
                  onClose={() => setEditModalOpen(false)}
                  aria-labelledby="modal-modal-title"
                  aria-describedby="modal-modal-description"
                >
                  <Box sx={editModalStyle}>
                    <Typography id="modal-modal-title" variant="h6" component="h2" sx={{ textAlign: 'center', mb: 2 }}>
                      Update User
                    </Typography>

                    <TextField fullWidth
                      name="username"
                      label="Name"
                      value={userData.username}
                      onChange={(e) => handleEditUserData(e)}
                      required
                      size="medium"
                      InputProps={{
                        startAdornment: <Person sx={{ color: 'text.secondary', mr: 1 }} />
                      }}
                      sx={{
                        mb: 4,
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2
                        }

                      }}
                    >
                    </TextField>

                    <TextField fullWidth
                      name="email"
                      label="Email"
                      value={userData.email}
                      onChange={(e) => handleEditUserData(e)}
                      required
                      size="medium"
                      InputProps={{
                        startAdornment: <Email sx={{ color: 'text.secondary', mr: 1 }} />
                      }}
                      sx={{
                        mb: 4,
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2
                        }

                      }}
                    >
                    </TextField>

                    <TextField fullWidth
                      name="phone"
                      label="Phone Number"
                      value={userData.phone}
                      onChange={(e) => handleEditUserData(e)}
                      required
                      size="medium"
                      InputProps={{
                        startAdornment: <Numbers sx={{ color: 'text.secondary', mr: 1 }} />
                      }}
                      sx={{
                        mb: 4,
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2
                        }

                      }}
                    >
                    </TextField>

                    <Box sx={{ textAlign: 'center', mb: 2 }}>
                      <Button onClick={updateUserData} variant='contained'>Update</Button>
                    </Box >



                  </Box>
                </Modal>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Mobile Bottom Navigation */}
      {isMobile && (
        <BottomNavigation
          value={activeSection}
          onChange={(event, newValue) => {
            if (newValue === 'menu') {
              setMobileDrawerOpen(true);
            } else {
              const item = navigationItems.find(nav => nav.id === newValue);
              if (item) handleNavigation(item);
            }
          }}
          sx={{
            position: 'fixed',
            bottom: 0,
            left: 0,
            right: 0,
            borderTop: '1px solid',
            borderColor: 'divider',
            bgcolor: 'background.paper',
          }}
        >
          {mobileNavItems.map((item) => (
            <BottomNavigationAction
              key={item.id}
              value={item.id}
              icon={item.icon}
              label={item.label}
              sx={{
                minWidth: 'auto',
                '& .MuiBottomNavigationAction-label': {
                  fontSize: '0.75rem',
                },
              }}
            />
          ))}
        </BottomNavigation>
      )}

      {/* Mobile Menu */}
      <Menu
        anchorEl={mobileMenuAnchor}
        open={Boolean(mobileMenuAnchor)}
        onClose={handleMobileMenuClose}
        PaperProps={{
          sx: {
            width: 200,
          },
        }}
      >
        <MenuItem onClick={() => handleNavigation(navigationItems.find(item => item.id === 'settings'))}>
          <ListItemIcon><Settings fontSize="small" /></ListItemIcon>
          Settings
        </MenuItem>
        <MenuItem onClick={() => handleNavigation(navigationItems.find(item => item.id === 'security'))}>
          <ListItemIcon><Security fontSize="small" /></ListItemIcon>
          Security
        </MenuItem>
      </Menu>
    </>
  );
};

// Updated Profile Section Component with mobile responsiveness
const ProfileSection = ({ userData, isMobile, editModalOpen, setEditModalOpen }) => (
  <Box sx={{ p: isMobile ? 2 : 4 }}>
    <Box sx={{
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: isMobile ? 'flex-start' : 'center',
      mb: 4,
      flexDirection: isMobile ? 'column' : 'row',
      gap: isMobile ? 2 : 0
    }}>
      <Typography variant={isMobile ? "h6" : "h5"} fontWeight="600">
        Profile Information
      </Typography>
      <Button
        onClick={() => setEditModalOpen(true)}
        variant="outlined" startIcon={<Edit />} size={isMobile ? "small" : "medium"}>
        Edit Profile
      </Button>
    </Box>

    <Grid container spacing={isMobile ? 2 : 4}>
      <Grid item xs={12}>
        <Card variant="outlined" sx={{ height: '100%' }}>
          <CardContent sx={{ p: isMobile ? 2 : 3 }}>
            <Typography variant={isMobile ? "subtitle1" : "h6"} gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Person fontSize={isMobile ? "small" : "medium"} /> Personal Information
            </Typography>
            <Divider sx={{ mb: 2 }} />

            <Box sx={{ '& > *:not(:last-child)': { mb: 1.5 } }}>
              <InfoRow label="Full Name" value={userData.username} isMobile={isMobile} />
              <InfoRow label="Email Address" value={userData.email} isMobile={isMobile} />
              <InfoRow label="Phone Number" value={userData.phone} isMobile={isMobile} />
              {/* <InfoRow label="Member Since" value={new Date(userData.created_at)} isMobile={isMobile} /> */}
            </Box>
          </CardContent>
        </Card>
      </Grid>

      <Grid item xs={12}>
        <Card variant="outlined" sx={{ height: '100%' }}>
          <CardContent sx={{ p: isMobile ? 2 : 3 }}>
            <Typography variant={isMobile ? "subtitle1" : "h6"} gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <History fontSize={isMobile ? "small" : "medium"} /> Activity Summary
            </Typography>
            <Divider sx={{ mb: 2 }} />

            <Box sx={{ '& > *:not(:last-child)': { mb: 1.5 } }}>
              <InfoRow label="Loyalty Points" value={userData.loyaltyPoints} isMobile={isMobile} />
              <InfoRow label="Total Orders" value="24" isMobile={isMobile} />
              <InfoRow label="Pending Orders" value="2" isMobile={isMobile} />
              <InfoRow label="Favorite Items" value="8" isMobile={isMobile} />
            </Box>
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  </Box>
);

// Updated Orders Section Component
const OrdersSection = ({ isMobile }) => (
  <Box sx={{ p: isMobile ? 2 : 4 }}>
    <Typography variant={isMobile ? "h6" : "h5"} fontWeight="600" mb={4}>
      My Orders
    </Typography>
    {/* <Box sx={{ textAlign: 'center', py: isMobile ? 4 : 8 }}>
      <ShoppingBag sx={{ fontSize: isMobile ? 48 : 64, color: 'text.secondary', mb: 2 }} />
      <Typography variant={isMobile ? "subtitle1" : "h6"} color="text.secondary" gutterBottom>
        No orders yet
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={3}>
        Your order history will appear here
      </Typography>
      <Button variant="contained" size={isMobile ? "small" : "medium"} onClick={() => window.location.href = '/'}>
        Start Ordering
      </Button>
    </Box> */}

    <ProfileOrderTable />
  </Box>
);

// Other section components with mobile support
const AddressesSection = ({ isMobile }) => {

  const [addressModalOpen, setAddressModalOpen] = useState(false)

  return (
    <Box sx={{ p: isMobile ? 2 : 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
        <Typography variant={isMobile ? "h6" : "h5"} fontWeight="600" mb={4}>
          My Addresses
        </Typography>

        <Button variant='contained' onClick={() => setAddressModalOpen(true)}>
          <Add /> Add Address
        </Button>

      </Box>

      <ProfileAddressTable />
      <AddAddressModal addressModalOpen={addressModalOpen} setAddressModalOpen={setAddressModalOpen} />
    </Box>

  )


};

const PaymentsSection = ({ isMobile }) => (
  <Box sx={{ p: isMobile ? 2 : 4 }}>
    <Typography variant={isMobile ? "h6" : "h5"} fontWeight="600" mb={4}>
      Payment Methods
    </Typography>
    {/* Add payments content */}
  </Box>
);

const FavoritesSection = ({ isMobile }) => (
  <Box sx={{ p: isMobile ? 2 : 4 }}>
    <Typography variant={isMobile ? "h6" : "h5"} fontWeight="600" mb={4}>
      My Favorites
    </Typography>
    {/* Add favorites content */}
  </Box>
);

const NotificationsSection = ({ isMobile }) => (
  <Box sx={{ p: isMobile ? 2 : 4 }}>
    <Typography variant={isMobile ? "h6" : "h5"} fontWeight="600" mb={4}>
      Notifications
    </Typography>
    {/* Add notifications content */}
  </Box>
);

const SecuritySection = ({ isMobile }) => (
  <Box sx={{ p: isMobile ? 2 : 4 }}>
    <Typography variant={isMobile ? "h6" : "h5"} fontWeight="600" mb={4}>
      Security
    </Typography>
    {/* Add security content */}
  </Box>
);

const SettingsSection = ({ isMobile }) => (
  <Box sx={{ p: isMobile ? 2 : 4 }}>
    <Typography variant={isMobile ? "h6" : "h5"} fontWeight="600" mb={4}>
      Settings
    </Typography>
    {/* Add settings content */}
  </Box>
);

// Updated reusable component for info rows with mobile support
const InfoRow = ({ label, value, isMobile }) => (
  <Box sx={{
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    py: isMobile ? 1 : 1.5
  }}>
    <Typography variant={isMobile ? "body2" : "body2"} color="text.secondary" fontWeight="500">
      {label}
    </Typography>
    <Typography variant={isMobile ? "body2" : "body2"} fontWeight="500" sx={{ textAlign: 'right' }}>
      {value}
    </Typography>
  </Box>
);


const AddAddressModal = ({ addressModalOpen, setAddressModalOpen }) => {


  const { showSuccess, showError } = useNotification();

  const [modalForm, setModalForm] = useState([
    { "id": 1, "name": "address_name", "label": "Address Name", icon: <Business sx={{ color: 'text.secondary', mr: 1 }} /> },
    { "id": 2, "name": "building_name", "label": "Building Name", icon: <AccountTree sx={{ color: 'text.secondary', mr: 1 }} /> },
    { "id": 3, "name": "building_number", "label": "Building Number", icon: <Numbers sx={{ color: 'text.secondary', mr: 1 }} /> },
    { "id": 4, "name": "street_address", "label": "Street Address", icon: <AddLocationAlt sx={{ color: 'text.secondary', mr: 1 }} /> },
    { "id": 5, "name": "pincode", "label": "Pincode", icon: <PinDrop sx={{ color: 'text.secondary', mr: 1 }} /> }
  ])

  const [form, setForm] = useState({
    "address_name": "",
    "building_name": "",
    "building_number": "",
    "street_address": "",
    "pincode": ""

  })
  const style = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: 400,
    bgcolor: 'background.paper',
    // border: '2px solid #000',
    boxShadow: 24,
    p: 4,
    borderRadius:'25px'
  };

  const handleAddressChange = (e) => {
    const updateData = {
      ...form,
      [e.target.name]: e.target.value
    }
    setForm(updateData)
  }

  const submitAddress = async () => {
    try {
      const response = await create_address(form);
      showSuccess("Address Created Successfully")
      setForm({
        "address_name": "",
        "building_name": "",
        "building_number": "",
        "street_address": "",
        "pincode": ""

      })
      setAddressModalOpen(false)
    }
    catch (error) {
      console.error("Error on Creating Address")
      showError("Something Went Wrong");

    }
  }

  return (
    <Modal
      open={addressModalOpen}
      onClose={() => setAddressModalOpen(false)}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
    >
      <Box sx={style}>
        <Typography variant='h5' sx={{ mb: 2, textAlign: 'center' }}>Add Address</Typography>
        {modalForm.map((items, index) => {
          return (
            <TextField fullWidth
              name={items.name}
              label={items.label}
              value={form[items.name]}
              onChange={(e) => handleAddressChange(e)}

              size="medium"
              InputProps={{
                startAdornment: items.icon
              }}
              sx={{
                mb: 4,
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2
                }

              }}
            >
            </TextField>
          )
        }


        )}
        <Box sx={{ textAlign: 'center' }}>
          <Button variant="contained" onClick={submitAddress}>Add Address</Button>
        </Box>

      </Box>
    </Modal>
  )
}

export default ProfilePage;